/**
 * Canonical job_type vocabulary — mirrors the backend `job_type` enum enforced
 * at the DB level on both `jobs` and `tickets` (API PR #203, migrations 0282 /
 * 0283).
 *
 * Single source of truth for the frontend. Import from here instead of
 * duplicating job type string literals, label maps, or ad-hoc title-casing
 * helpers in individual files.
 *
 * NOTE: on `jobs`, `service_type` is a SEPARATE free-text column with a
 * per-vertical vocabulary (see `src/contexts/IndustryContext.tsx`). It is NOT
 * job_type and must NOT be rendered through this map. On `tickets`, the
 * frontend alias `serviceType` IS job_type — that one does map through here.
 */

/** All job types the backend recognises. */
export type JobType =
  | 'surface'
  | 'intermediate'
  | 'production'
  | 'liner'
  | 'tie_back'
  | 'liner_and_tie_back'
  | 'completions'
  | 'pulling_jacks'
  | 'push_down_jacks'
  | 'catwalk'
  | 'wash_truck';

/**
 * Every job type, in the backend enum's own order: the casing program first
 * (shallowest to deepest, then the combined run), then the non-casing services
 * appended by API PR #322 / migration 0322 at sortorder 7-10.
 *
 * This order is asserted against the DB enum order by a test on the API side.
 * Do not reorder and do not alphabetise — append only.
 *
 * Any UI that enumerates job types (pickers, filters) should iterate this so
 * nothing is silently unreachable.
 */
export const JOB_TYPES: readonly JobType[] = [
  'surface',
  'intermediate',
  'production',
  'liner',
  'tie_back',
  'liner_and_tie_back',
  'completions',
  'pulling_jacks',
  'push_down_jacks',
  'catwalk',
  // API migration 0362. Appended, like 0322's four.
  'wash_truck',
] as const;

/** Human-readable labels keyed by JobType. */
export const JOB_TYPE_LABELS: Record<JobType, string> = {
  surface: 'Surface',
  intermediate: 'Intermediate',
  production: 'Production',
  liner: 'Liner',
  tie_back: 'Tie-Back',
  liner_and_tie_back: 'Liner & Tie-Back',
  completions: 'Completions',
  pulling_jacks: 'Pulling Jacks',
  push_down_jacks: 'Push Down Jacks',
  catwalk: 'Catwalk',
  wash_truck: 'Wash Truck',
};

/** Rendered when job_type is NULL / absent. The column is nullable and the field is optional. */
export const JOB_TYPE_EMPTY_LABEL = '—';

/**
 * Resolve a stored value to a canonical JobType, or null if it is not in the
 * vocabulary.
 *
 * Tolerant on READ only, and only over separator/casing variance — this exists
 * because migrations 0282/0283 land on a Railway deploy that is independent of
 * this app's deploy, so during that window rows still hold pre-migration
 * display text ("Production", "Liner And Tie Back", "Tie-Back").
 *
 * It is deliberately NOT fuzzy. Values that are genuinely outside the
 * vocabulary ("prod", "production_casing", "Surface Casing") resolve to null so
 * they surface as a visible problem rather than being laundered into a
 * plausible-looking label. Writers must always emit canonical codes.
 */
export function toJobType(raw: string | null | undefined): JobType | null {
  if (!raw) return null;
  const normalized = String(raw)
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  return (JOB_TYPES as readonly string[]).includes(normalized)
    ? (normalized as JobType)
    : null;
}

/**
 * Display label for a stored job_type.
 *
 * - NULL / empty  -> an em dash (the field is optional; this is a real state)
 * - canonical     -> its label
 * - anything else -> `Unknown (<raw>)`, so bad data is visible rather than
 *                    silently blank or title-cased into looking legitimate
 */
export function getJobTypeLabel(raw: string | null | undefined): string {
  if (!raw) return JOB_TYPE_EMPTY_LABEL;
  const canonical = toJobType(raw);
  if (canonical) return JOB_TYPE_LABELS[canonical];
  return `Unknown (${raw})`;
}

/**
 * Fold a string into the form used for job_type text search: lowercase, `&`
 * spelled out, every separator (`_`, `-`, whitespace) collapsed to a single
 * space.
 *
 * This is what lets the stored code and its label meet in the middle —
 * `liner_and_tie_back` and `Liner & Tie-Back` both fold to `liner and tie back`,
 * so a user can type either one (or `tie back`, or `liner`) and hit the row.
 *
 * NOT a label map and NOT a display helper — the output is never rendered.
 */
export function foldJobTypeSearch(value: string | null | undefined): string {
  if (!value) return '';
  return String(value)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[\s_-]+/g, ' ')
    .trim();
}

/**
 * Does a stored job_type match a free-text search query?
 *
 * Matches on BOTH the stored code and the rendered label. Matching only the
 * label would break anyone who searches by the code they know from the API or
 * an export; matching only the code (the pre-fix behaviour) breaks everyone who
 * searches for the text actually on their screen. Neither group should lose.
 *
 * Unmapped values still match on their raw text, via the `Unknown (<raw>)`
 * label — a bad row stays findable.
 */
export function jobTypeMatchesSearch(
  raw: string | null | undefined,
  query: string,
): boolean {
  const needle = foldJobTypeSearch(query);
  if (!needle) return false;
  const haystack = `${foldJobTypeSearch(raw)} ${foldJobTypeSearch(getJobTypeLabel(raw))}`;
  return haystack.includes(needle);
}

/** Options shaped for `<select>` / picker components. */
export const JOB_TYPE_OPTIONS: readonly { value: JobType; label: string }[] =
  JOB_TYPES.map(value => ({ value, label: JOB_TYPE_LABELS[value] }));
